import React from 'react';
import PropTypes from 'prop-types';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useSnackbar } from 'notistack';
import MediaTile from './MediaTile';
import {
    resolvePreviewUrl, makePendingPlaceholder, useObjectUrlGc, itemMatchesType, pendingIndexOf
} from './mediaLogic';
import { validateMediaFile } from '../../../../../views/democracy/utils';
import './media.css';

const SECTIONS = [['IMAGE', 'Изображения'], ['AUDIO', 'Аудио'], ['VIDEO', 'Видео']];

// Стабильный id для сортировки: по id загруженного медиа, иначе по preview-url нового файла.
const keyOf = (item) => (item && item.id != null ? `id-${item.id}` : `pu-${item && item.__previewUrl}`);

function SortableMediaTile({ sortId, type, url, onRemove, disabled }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sortId, disabled });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
        cursor: disabled ? 'default' : 'grab'
    };
    return (
        <div ref={setNodeRef} style={style} className="media-sortable" {...attributes} {...listeners}>
            <MediaTile type={type} url={url} onRemove={onRemove} disabled={disabled} />
        </div>
    );
}

SortableMediaTile.propTypes = {
    sortId: PropTypes.string.isRequired,
    type: PropTypes.string,
    url: PropTypes.string,
    onRemove: PropTypes.func,
    disabled: PropTypes.bool
};

// Контролируемый множественный медиа-виджет (медиа вопроса). Секции по типам, drag-сортировка.
export default function AppMediaList({ value, pendingFiles, onChange, type, disabled }) {
    useObjectUrlGc(value);
    const { enqueueSnackbar } = useSnackbar();
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const typeArray = type.split(',').map(t => t.trim());
    const showAll = typeArray.includes('ALL');

    const handleAppend = (file) => {
        const validationError = validateMediaFile(file);
        if (validationError) {
            enqueueSnackbar(validationError, { variant: 'error' });
            return;
        }
        onChange([...value, makePendingPlaceholder(file)], [...pendingFiles, file]);
    };

    const handleRemove = (item) => {
        const idx = value.indexOf(item);
        if (idx < 0) return;
        const nextValue = value.filter((_, i) => i !== idx);
        const pIdx = item.__pending ? pendingIndexOf(value, item) : -1;
        const nextPending = pIdx >= 0 ? pendingFiles.filter((_, i) => i !== pIdx) : pendingFiles;
        onChange(nextValue, nextPending);
    };

    const handleReorder = (sectionType, { active, over }) => {
        if (disabled || !over || active.id === over.id) return;
        const typeItems = value.filter(it => itemMatchesType(it, sectionType));
        const from = typeItems.findIndex(it => keyOf(it) === active.id);
        const to = typeItems.findIndex(it => keyOf(it) === over.id);
        if (from < 0 || to < 0 || from === to) return;
        const reordered = arrayMove(typeItems, from, to);
        // Перестраиваем плоский value: type-слоты заполняем переупорядоченными, остальные — на месте.
        let k = 0;
        const nextValue = value.map(it => (itemMatchesType(it, sectionType) ? reordered[k++] : it));
        // Сохраняем инвариант «i-й pending-элемент ↔ i-й файл», иначе новые фото привяжутся не к тем файлам.
        const fileByItem = new Map(value.filter(it => it.__pending).map((it, i) => [it, pendingFiles[i]]));
        const nextPending = nextValue.filter(it => it.__pending).map(it => fileByItem.get(it));
        onChange(nextValue, nextPending);
    };

    const sections = SECTIONS.filter(([t]) => showAll || typeArray.includes(t));

    return (
        <div className="media-sections">
            {sections.map(([t, title]) => {
                const items = value.filter(it => itemMatchesType(it, t));
                return (
                    <div className="media-col" key={t}>
                        <h5>{title}</h5>
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleReorder(t, e)}>
                            <SortableContext items={items.map(keyOf)} strategy={verticalListSortingStrategy}>
                                <div className="stack">
                                    {items.map((item) => (
                                        <SortableMediaTile
                                            key={keyOf(item)}
                                            sortId={keyOf(item)}
                                            type={t}
                                            url={resolvePreviewUrl(item)}
                                            onRemove={() => handleRemove(item)}
                                            disabled={disabled}
                                        />
                                    ))}
                                    {!disabled && <MediaTile type={t} url={null} onSelect={handleAppend} />}
                                </div>
                            </SortableContext>
                        </DndContext>
                    </div>
                );
            })}
        </div>
    );
}

AppMediaList.propTypes = {
    value: PropTypes.array,
    pendingFiles: PropTypes.array,
    onChange: PropTypes.func.isRequired,
    type: PropTypes.string,
    disabled: PropTypes.bool
};

AppMediaList.defaultProps = { value: [], pendingFiles: [], type: 'ALL', disabled: false };
